import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, RefreshCw, Eye, Loader2 } from 'lucide-react'
import { Pagination, PAGINATION_PAGE_SIZE } from '@/components/Pagination'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useSources } from '@/hooks/use-sources'
import { useAuth } from '@/contexts/auth'
import { hasPermission } from '@/utils/permissions'

export default function SourcesPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const navigate = useNavigate()
  
  const { sources, total, isLoading, syncSources } = useSources({
    offset: (currentPage - 1) * PAGINATION_PAGE_SIZE,
    limit: PAGINATION_PAGE_SIZE,
    q: searchTerm || undefined,
  })

  const { user } = useAuth()
  const canEdit = hasPermission(user, 'edit_sources')

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-300 text-secondary-foreground">Ativo</Badge>
      case 'INACTIVE':
        return <Badge className="bg-gray-100 text-secondary-foreground">Inativo</Badge>
      case 'ERROR':
        return <Badge className="bg-red-100 text-secondary-foreground">Erro</Badge>
      default:
        return <Badge className="bg-gray-100 text-secondary-foreground">Desconhecido</Badge>
    }
  }

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-4">
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-gray-900">Fontes</h2>
          <p className="text-muted-foreground">Gerencie e controle as fontes do Culture Monitor.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative w-full sm:w-auto">
            <Input
              placeholder="Procurar"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 w-full sm:w-64"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 stroke-gray-400 w-4 h-4" />
          </div>
          <Button variant="dark" onClick={syncSources} disabled={isLoading} className="hidden w-full sm:w-auto">
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            <span className="hidden sm:inline">Sincronizar Fontes</span>
            <span className="sm:hidden">Sincronizar</span>
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fontes de Dados</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Última Sincronização</TableHead>
            <TableHead>Status</TableHead>
            {canEdit && <TableHead>Ações</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={canEdit ? 5 : 4} className="p-8 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Sincronizando fontes...</span>
                </div>
              </TableCell>
            </TableRow>
          ) : sources.length === 0 ? (
            <TableRow>
              <TableCell colSpan={canEdit ? 5 : 4} className="p-8 text-center text-gray-500">
                Nenhuma fonte encontrada
              </TableCell>
            </TableRow>
          ) : (
            sources.map((source) => (
              <TableRow key={source.id}>
                <TableCell className="p-4">
                  <span className="font-medium text-gray-900">{source.name}</span>
                </TableCell>
                <TableCell className="p-4 text-gray-600">{source.type}</TableCell>
                <TableCell className="p-4 text-gray-600">{formatDate(source.updated_at)}</TableCell>
                <TableCell className="p-4">
                  {getStatusBadge(source.status)}
                </TableCell>
                {canEdit && (
                  <TableCell className="p-4">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="bg-secondary hover:bg-gray-200"
                      onClick={() => navigate(`/app/settings/sources/${source.id}`)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Inspecionar Fonte
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!isLoading && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalItems={total}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  )
}